'use client';
import React, { useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from '@/lib/redux/hooks';
import { addToCart } from '@/lib/redux/features/cart';
import { toast } from 'react-toastify';
import axios from '@/lib/axios';
import { useAuth } from '@/features/authentication/hooks/auth';
import { usePathname, useRouter } from 'next/navigation';
import Link from 'next/link';
import { PiShoppingCartLight } from 'react-icons/pi';
import { pushToDataLayer } from '@/features/gtm/gtmUtils';
import NextImage from '@/features/Image';
import { validateCartPreorder } from '@/utils/cartUtils';

const CustomToast = ({ product, btn_size = 'md' }: any) => {
  return (
    <div className="custom-toast">
      <div className="d-flex gap-3 align-items-center">
        <NextImage
          width={64}
          height={64}
          src={product?.small_pictures?.[0]?.url}
          alt="Product"
        />
        <p> {product?.name}</p>
      </div>
      <div className="d-flex gap-2 mt-2">
        <div>
          {' '}
          <Link
            href={'/cart'}
            className={`trk-btn trk-btn--sm trk-btn--outline`}
          >
            View Cart
          </Link>
        </div>
        <div>
          <Link
            href={'/checkout'}
            className={`trk-btn trk-btn--sm trk-btn--primary`}
          >
            Checkout
          </Link>
        </div>
      </div>
    </div>
  );
};

const CartButton = ({
  isIcon = false,
  product,
  btn_size = 'md',
  className = `trk-btn trk-btn--${btn_size} trk-btn--outline`,
}: any) => {
  const { user } = useAuth();
  const router = useRouter();
  const [loading, setLoading] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const despatch = useAppDispatch();
  const cartItems = useAppSelector((state) => state.cart.items);
  const pathname = usePathname();

  useEffect(() => {
    const checkIfMobile = () => {
      setIsMobile(window.innerWidth < 992);
    };

    checkIfMobile();
    window.addEventListener('resize', checkIfMobile);

    return () => window.removeEventListener('resize', checkIfMobile);
  }, []);

  const handleClick = async () => {
    const cartItem = cartItems.find((item) => item.id === product.id);

    let quantity = 0;
    if (cartItem) {
      quantity = cartItem.quantity + product.quantity;
      if (product.max_qty && quantity > product.max_qty) {
        toast.error(`You can add maximum ${product.max_qty} quantity`);
        return;
      } else if (quantity > product.stock) {
        toast.error(`You can add maximum ${product.stock} quantity`);
        return;
      }
    } else {
      quantity = product.quantity;
      if (product.max_qty && quantity > product.max_qty) {
        toast.error(`You can add maximum ${product.max_qty} quantity`);
        return;
      } else if (quantity > product.stock) {
        toast.error(`You can add maximum ${product.stock} quantity`);
        return;
      }
    }

    const isValid = validateCartPreorder(cartItems, product);
    if (!isValid) {
      toast.error("You can't add a preorder item and a regular item together");
      setLoading(false);
      return;
    }
    setLoading(true);

    try {
      const res = await despatch(addToCart(product)).unwrap();
      if (res?.result) {
        toast(<CustomToast product={product} />, {
          position: 'bottom-right',
          closeButton: false,
          autoClose: 1000,
        });
        pushToDataLayer('add_to_cart', {
          ecommerce: {
            member_id: user?.data?.id || null,
            item_id: product?.id,
            value: product?.price,
            currency: 'BDT',
            quantity: 1,
          },
        });
      } else {
        toast.error(res?.message || 'Something went wrong');
      }
    } catch (error) {
      console.log(error);
      toast.error('Failed to add to cart');
    } finally {
      setLoading(false);
    }
  };

  const handlePreOrder = async () => {
    // if (!user) {
    //   return router.push('/login');
    // }
    setLoading(true);

    const isValid = validateCartPreorder(cartItems, product);
    if (!isValid) {
      toast.error("You can't add a preorder item and a regular item together");
      setLoading(false);
      return;
    }

    try {
      const res = await despatch(
        addToCart({ ...product, preorder_available: 1 })
      ).unwrap();
      if (res?.result) {
        toast(<CustomToast product={product} />, {
          position: 'bottom-right',
          closeButton: false,
        });
        pushToDataLayer('add_to_cart', {
          ecommerce: {
            member_id: user?.data?.id || null,
            item_id: product?.id,
            value: product?.price,
            currency: 'BDT',
            quantity: 1,
          },
        });
      } else {
        toast.error(res?.message || 'Something went wrong');
      }
    } catch (error) {
      console.log(error);
      toast.error('Failed to add to cart');
    } finally {
      setLoading(false);
    }
  };

  const requestStock = async () => {
    if (!user) return router.push(`/login?redirect=${pathname}`);
    try {
      const response = await axios.post(
        `/api/v2/add-product-request/${product.id}`
      );
      if (response?.data.result) {
        toast.success(response.data?.message);
      } else {
        toast.warning(response?.data?.message);
      }
    } catch (error: any) {
      console.log(error);
      toast.error(error?.message || 'Failed to add product to request');
    }
  };

  const loadingText = isIcon ? 'add..' : 'Adding.....';

  return (
    <>
      {product.stock > 0 ? (
        (() => {
          const isInCart = cartItems.some(
            (item) => item.product_id === product.id
          );
          if (isInCart) {
            return (
              <Link
                href="/cart"
                className={`trk-btn trk-btn--primary trk-btn--md`}
              >
                View Cart
              </Link>
            );
          }

          return (
            <span
              onClick={() => handleClick()}
              className={`${className} ${isIcon && isMobile && 'border-0 p-0 fw-semibold'}`}
            >
              {loading ? (
                loadingText
              ) : isIcon && isMobile ? (
                <PiShoppingCartLight size={22} className="m-0" />
              ) : (
                'Add to cart'
              )}
            </span>
          );
        })()
      ) : product.preorder_available ? (
        <span
          onClick={() => handlePreOrder()}
          className={`trk-btn trk-btn--${btn_size} trk-btn--pre-order`}
        >
          {loading ? 'Loading...' : 'Preorder Now'}
        </span>
      ) : product.request_available === 1 ? (
        <span
          onClick={() => requestStock()}
          className={`trk-btn trk-btn--${btn_size} trk-btn--request-stock`}
        >
          {loading ? 'Loading...' : 'Request For Stock'}
        </span>
      ) : (
        <span
          className={`trk-btn trk-btn--${btn_size} trk-btn--stock-out ${isIcon && isMobile && 'd-none'} `}
        >
          {isIcon && isMobile ? '✖' : 'Out of stock'}
        </span>
      )}
    </>
  );
};

export default CartButton;
